<template>
  <div class="otb-summary" :class="{ 'otb-summary__usabilla': hideBacklink }">
    <div :class="containerClass">
      <div class="wb-grid-row">
        <div
          class="otb-summary__first-container wb-grid-col-mq1-12 wb-grid-col-mq5-10 wb-grid-col-offset-mq5-1"
        >
          <div class="otb-summary__section wb-card wb-card--white">
            <div class="otb-summary__header-section">
              <div
                class="wb-grid-col-mq1-12 wb-grid-col-mq3-10 wb-grid-col-offset-mq3-1"
              >
                <summary-header
                  class="otb-summary__header"
                  :preTitle="headerPreTitle"
                  :title="headerTitle"
                  :postTitle="headerPostTitle"
                />
              </div>
            </div>
            <div class="otb-summary__dealer-info">
              <div
                class="wb-grid-col-mq1-12 wb-grid-col-mq3-10 wb-grid-col-offset-mq3-1"
              >
                <summary-dealer-info
                  :dateAndPlace="dateAndPlace"
                  :dealerInformation="dealerInformation"
                />
              </div>
            </div>
            <div class="otb-summary__vehicle-info">
              <div
                class="wb-grid-col-mq1-12 wb-grid-col-mq3-8 wb-grid-col-offset-mq3-2"
              >
                <summary-vehicle-information
                  :status="status"
                  :vehicleInformation="vehicleInformation"
                  :path="path"
                  :context="context"
                />
              </div>
            </div>
            <div class="otb-summary__license-warning">
              <summary-license-warning
                :message="summaryLicenseWarningMessage"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="teaserVisible" class="wb-grid-row">
        <div
          class="wb-grid-col-mq1-12 wb-grid-col-mq5-10 wb-grid-col-offset-mq5-1"
        >
          <div
            class="otb-summary__section otb-summary__section-teaser wb-card wb-card--white"
          >
            <summary-teaser
              :buttonLabel="$t('summary.teaser.button')"
              :buttonLink="teaserLink"
              :imageSrc="
                teaserImage ||
                require(`../../assets/test-drive-mercedes-passenger-car-front.png`)
              "
              :title="$t('summary.teaser.title')"
              :message="$t('summary.teaser.copy')"
            />
          </div>
        </div>
      </div>
      <div class="wb-grid-row">
        <div
          class="wb-grid-col-mq1-12 wb-grid-col-mq5-10 wb-grid-col-offset-mq5-1"
        >
          <div
            class="otb-summary__section otb-summary__footer wb-card wb-card--white"
            :class="{ 'otb-summary__footer-usabilla': hideBacklink }"
          >
            <summary-personal-details :personalDetails="personalDetails" />
            <div class="wb-grid-row">
              <div
                class="otb-summary__footnotes-container wb-grid-col-mq1-12 wb-grid-col-mq3-10 wb-grid-col-offset-mq3-1"
              >
                <footnotes
                  v-if="showFootNotes"
                  class="otb-summary__footnotes"
                  :body="$t('shared.footnotes.text')"
                  :title="$t('shared.footnotes.title')"
                  hasCardStyle
                />
              </div>
            </div>
            <div class="wb-grid-row">
              <div
                class="otb-summary__post-scriptum-container wb-grid-col-mq1-10 wb-grid-col-offset-mq1-1"
              >
                <p class="otb-summary__post-scriptum wb-type-hint">
                  {{ $t('summary.booking.cancellation.info') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="wb-grid-row">
        <div
          v-if="!hideBacklink"
          id="otb-summary__backlink"
          class="otb-summary__backlink wb-grid-col-mq1-10 wb-grid-col-offset-mq1-1"
        >
          <button
            type="button"
            class="wb-button wb-button--secondary wb-button--large"
            @click="backlink"
          >
            {{ backlinkButtonLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue';

import { TranslateResult } from 'vue-i18n';

import { i18n } from '../../services/translation.service';

import { TestDriveModeSummaryPostTitleLabel } from '@dh-io-otb/shared/src/models/utilities/testdrive-mode.enum';
import { TestDriveMode } from '../../models/utilities/testdrive-mode.enum';
import { useProspect } from '../../composables/prospect.composable';

import Footnotes from '../footnotes/index.vue';
import SummaryDealerInfo from './sections/summary-dealer-info/summary-dealer-info.vue';
import SummaryHeader from './sections/summary-header/summary-header.vue';
import SummaryLicenseWarning from './sections/summary-license-warning/summary-license-warning.vue';
import SummaryPersonalDetails from './sections/summary-personal-details/summary-personal-details.vue';
import SummaryTeaser from './sections/summary-teaser/summary-teaser.vue';
import SummaryVehicleInformation from './sections/summary-vehicle-info/summary-vehicle-info.vue';
import { useStore } from '../../composables/store.composable';
import { WEIGHT_CATEGORY } from '@dh-io-otb/otbc/src/models/constants';

export default defineComponent({
  name: 'otb-summary-page',
  props: {
    backlink: {
      type: Function as any,
    },
    hideBacklink: {
      type: Boolean,
    },
    backlinkButtonLabel: String as () => string | TranslateResult,
    dateAndPlace: String,
    dealerInformation: {
      type: Object,
      required: true,
    },
    personalDetails: {
      type: Object,
      required: true,
    },
    teaserImage: String,
    teaserLink: String,
    teaserVisible: Boolean,
    status: {
      type: String,
      required: true,
      default: TestDriveMode.REQUEST,
    },
    vehicleInformation: {
      type: Object,
      required: true,
    },
    wrapInContainer: {
      type: Boolean,
      default: false,
    },
    path: {
      type: String,
      default: '',
    },
  },
  components: {
    Footnotes,
    SummaryDealerInfo,
    SummaryHeader,
    SummaryLicenseWarning,
    SummaryPersonalDetails,
    SummaryTeaser,
    SummaryVehicleInformation,
  },
  setup(props) {
    const { buildProspectTitle } = useProspect();
    const { state, getter } = useStore();

    const containerClass = computed((): string => {
      return props.wrapInContainer ? 'wb-grid-container' : '';
    });

    // Must change when/if rental is available
    const headerPreTitle = computed(
      (): TranslateResult =>
        i18n.t(`summary.rentalNotAvailable.${props.status.toLowerCase()}.title`)
    );

    const headerPostTitle = computed((): TranslateResult => {
      if (getter?.isTestDriveFromHomeEnabled)
        return i18n.t(`summary.headline.testDriveFromHome.label`);
      return i18n.t(
        `summary.headline.copy${
          TestDriveModeSummaryPostTitleLabel[props.status]
        }.label`
      );
    });

    const headerTitle = computed((): string =>
      buildProspectTitle(props.personalDetails?.customer)
    );

    const showFootNotes = computed((): boolean => {
      return (
        state.configuration.footNotes?.visible &&
        (state.otbcFlow.selectedVehicle?.motorisationId ||
          state.otbcFlow.selectedMotorisation)
      );
    });

    const context = computed((): boolean => {
      return state.configuration.context;
    });
    const summaryLicenseWarningMessage = computed((): TranslateResult => {
      return state.otbcFlow.selectedMotorisation.weightCategory ===
        WEIGHT_CATEGORY.HEAVY_WEIGHT ||
        state.otbcFlow.selectedWeight === WEIGHT_CATEGORY.HEAVY_WEIGHT
        ? i18n.t('summary.info.heavyWeight.license')
        : i18n.t('summary.info.license');
    });
    return {
      containerClass,
      headerPostTitle,
      headerPreTitle,
      headerTitle,
      context,
      showFootNotes,
      summaryLicenseWarningMessage,
    };
  },
});
</script>
<style lang="scss" src="./summary-page.scss"></style>
